import { Layout, Menu } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountStore } from 'store/accountStore';
import { AccountInterface } from 'type/accountType';

import {
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  TOOLS_MENU_ITEMS,
  INGEST_MENU_ITEMS,
  META_DATA_STRUCTURE_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  SYNC_MENU_ITEMS,
  VENDOR_MENU_ITEMS,
} from 'constants/menu';
import './Sidebar.css';

export interface SidebarProps {
  currentPath: string;
  defaultOpenKeys: string[];
  children: React.ReactNode;
}

const { Content, Footer, Sider } = Layout;

const Sidebar: React.FC<SidebarProps> = observer(({ children, currentPath, defaultOpenKeys }) => {
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [, setUser] = useState<AccountInterface | null>(null);
  const menus = accountStore.getMenus();
  const contentMenuList = useMemo(
    () => CONTENT_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const contractMenuList = useMemo(
    () => CONTRACT_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const revenueMenuList = useMemo(
    () => REVENUE_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const metadataMenuList = useMemo(
    () => META_DATA_STRUCTURE_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  // Ingest 메뉴는 권한 체크 없이 모두에게 표시
  const ingestMenuList = useMemo(
    () => INGEST_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  // Debug 메뉴는 Admin role(is_staff)만 표시
  const toolsMenuList = useMemo(
    () => TOOLS_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const syncMenuList = useMemo(
    () => SYNC_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  const vendorMenuList = useMemo(
    () => VENDOR_MENU_ITEMS!.filter(item => menus.includes(item!.key as string)),
    [menus]
  );

  // 벤더 role 전용 사용자: 사이드바에 Vendor 섹션 하나만 노출됨 → 상단 구분선 제거해서 허전함 완화.
  // (일반 어드민은 여러 섹션 구분선이 의미 있어 유지)
  const isVendorOnly =
    vendorMenuList.length > 0 &&
    contentMenuList.length === 0 &&
    contractMenuList.length === 0 &&
    revenueMenuList.length === 0 &&
    metadataMenuList.length === 0 &&
    ingestMenuList.length === 0 &&
    syncMenuList.length === 0 &&
    toolsMenuList.length === 0;

  useEffect(() => {
    const user = accountStore.getAccount();
    if (user) {
      setImageUrl(user.platform.logo);
      setUser(user);
    }
  }, []);

  return (
    <Layout className="main-layout" hasSider>
      <Sider className="sider" width={200}>
        <button
          className="logo-button"
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <img src={imageUrl} className="logo-image" alt="main_logo" />
        </button>
        {!isVendorOnly && <div className="menu-header"></div>}
        {contentMenuList.length > 0 && (
          <>
            <div className="content-menu-header">
              <div>Content</div>
            </div>
            <Menu
              className="menu"
              theme="dark"
              mode="inline"
              items={contentMenuList}
              selectedKeys={[currentPath]}
              defaultOpenKeys={defaultOpenKeys}
            />
          </>
        )}
        <>
          {metadataMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Metadata</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={metadataMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {ingestMenuList.length > 0 && (
            <div>
              <div className="ingest-menu-header">
                <div>Ingest</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={ingestMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {revenueMenuList.length > 0 && (
            <div>
              <div className="revenue-menu-header">
                <div>Revenue</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={revenueMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {contractMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Contract</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={contractMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {vendorMenuList.length > 0 && (
            <div>
              <div className={isVendorOnly ? 'content-menu-header' : 'contract-menu-header'}>
                <div>Vendor</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={vendorMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {syncMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>ODX Sync Monitor</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={syncMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
          {toolsMenuList && toolsMenuList.length > 0 && (
            <div>
              <div className="contract-menu-header">
                <div>Tools</div>
              </div>
              <Menu
                className="menu"
                theme="dark"
                mode="inline"
                items={toolsMenuList}
                selectedKeys={[currentPath]}
                defaultOpenKeys={defaultOpenKeys}
              />
            </div>
          )}
        </>
      </Sider>
      <Layout className="layout-content">
        <Content className="content">{children}</Content>
        <Footer className="footer">ODKMedia, Inc</Footer>
      </Layout>
    </Layout>
  );
});

export default React.memo(Sidebar);
